import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Avatar } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { setData } from '../../store/Home/authSlice';
import { getCookie } from '../../services/api';
import { preserveRedirectInPath } from '../../utils/redirect';
import { PARAMS } from '../../params';

const CorpSelectionPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    // Auth slice shape: token is at top-level `accessToken` (camelCase), NOT
    // at userData.accesstoken — that path doesn't exist in the slice.
    const { userData, accessToken, refreshToken } = useSelector((state: any) => state.auth);
    const corporationDetails: any[] = userData?.corporation_details || [];
    const email: string = userData?.email || '';
    const [selectedCorp, setSelectedCorp] = useState<any>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!corporationDetails.length || !email) {
            navigate('/signin-new');
        }
    }, []);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Enter' && selectedCorp && !loading) {
                handleProceed();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedCorp, loading]);

    const handleProceed = () => {
        if (!selectedCorp || loading) return;
        dispatch(setData({
            data: { corporation_details: corporationDetails, selected_corp: selectedCorp, email },
            // Note: getCookie returns null for HttpOnly cookies on deployed (.veytan.com).
            // We keep these reads as a best-effort for any non-HttpOnly fallback the
            // legacy localhost code path might write. The downstream consumers should
            // prefer the accesstoken from signin response in Redux.
            accesstoken: getCookie(PARAMS.TOKEN_STORAGE_KEY) || accessToken || '',
            refreshtoken: getCookie(PARAMS.TOKEN_STORAGE_KEY_REFRESH) || refreshToken || ''
        }));
        navigate(preserveRedirectInPath('/legal-entity-selection', location), { state: { showBack: true } });
    };

    return (
        <div className="min-h-screen bg-[#F9FAFB] relative">
            <div
                className="absolute inset-0 pointer-events-none opacity-50"
                style={{
                    backgroundImage: 'url(/assets/login/workspace-pattern.svg)',
                    backgroundRepeat: 'repeat'
                }}
            />

            <div className="absolute top-0 left-0 w-full text-2xl text-[#6941C6] font-[Montserrat Alternates] font-bold leading-normal pt-6 pl-9 tracking-[-1.12px]">
                Veytan
            </div>

            <div className="flex justify-center items-center min-h-screen">
                <div className="w-full max-w-[480px] px-4 relative z-10">
                    <div className="w-full bg-white rounded-2xl shadow-[0px_0px_8px_rgba(0,0,0,0.03)]">
                        <div className="px-6 pt-6 pb-4">
                            <h1 className="text-[28px] font-semibold text-[#101828]">Workspaces</h1>
                            <p className="text-base text-[#475467]">Choose a parent corporation to continue</p>
                        </div>

                        <div className="px-4 space-y-1">
                            {corporationDetails.map((corp: any) => (
                                <div
                                    key={corp.id}
                                    className={`group cursor-pointer rounded-lg transition-all duration-200 hover:bg-[#F9FAFB] ${
                                        selectedCorp?.id === corp.id ? 'bg-[#F9FAFB]' : ''
                                    }`}
                                    onClick={() => setSelectedCorp(corp)}
                                >
                                    <div className="flex items-center justify-between py-2.5 px-2">
                                        <div className="flex items-center gap-3">
                                            <div className="flex-shrink-0">
                                                <Avatar
                                                    src={corp.logo_url?.[0]?.presigned_url || '/assets/login/company-default.svg'}
                                                    size={32}
                                                    className="bg-[#F4EBFF]"
                                                    style={{ border: '1px solid #F9F5FF' }}
                                                />
                                            </div>
                                            <p className="text-sm font-medium text-[#101828]">{corp.full_name}</p>
                                        </div>
                                        <RightOutlined className="text-[#667085]" />
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="p-4">
                            <button
                                onClick={handleProceed}
                                disabled={!selectedCorp || loading}
                                className={`w-full py-2.5 px-4 rounded-lg text-sm font-semibold transition-colors ${
                                    selectedCorp && !loading
                                        ? 'bg-[#7F56D9] text-white hover:bg-[#6941C6]'
                                        : 'bg-[#F2F4F7] text-[#D0D5DD] cursor-not-allowed'
                                }`}
                            >
                                {loading ? 'Loading...' : 'Proceed to login'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CorpSelectionPage;
